import {
  FormControl,
  FormHelperText,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Controller, useFormContext } from 'react-hook-form';
import { DynamicIcon, type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import type { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { useWatchConditions } from 'src/components/Forms/use-validations';

export interface FormToggleGroupOption {
  id: string;
  value: string;
  label: string;
  icon?: string;
  iconLibrary?: IconLibrary;
}

/**
 * Deliberately narrow rather than an `Omit<ToggleButtonGroupProps, …>`: this type
 * joins the `FormField` union, and `get-form-field` destructures shared props off
 * that union — every extra MUI prop pulled in here collides with another member's
 * (`size` alone: MUI's toggle allows `large`, the form control does not).
 * Group-level props go through `toggleGroupProps`.
 */
export type FormToggleGroupProps = {
  label?: string;
  options: FormToggleGroupOption[];
  value?: string;
  disabled?: boolean;
  onChange?: ToggleButtonGroupProps['onChange'];
  sx?: SxProps<Theme>;
  toggleGroupProps?: Partial<ToggleButtonGroupProps>;
} & CommonFormFieldProps<'toggle-group', string>;

const ICON_CLASS_NAME = 'form-toggle-group-icon';

/**
 * The selected treatment is a plain raised surface with the icon carrying the only
 * colour, lifted from the snippet-source toggle macros shipped. The theme paints
 * selected toggles with the secondary purple through a
 * `.MuiToggleButtonGroup-root .Mui-selected` rule, which outranks an `sx` on the
 * button itself — so the neutral override has to be set here, at the group level.
 */
const groupSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
  '& .MuiToggleButton-root': {
    borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
  },
  '& .MuiButtonBase-root:hover': {
    backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
  },
  '& .MuiButtonBase-root.Mui-selected': {
    backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHighest,
    color: (theme) => theme.palette.custom.OV.sys.onSurface,
    [`& .${ICON_CLASS_NAME}`]: {
      color: (theme) => theme.palette.custom.OV.sys.tertiary,
    },
    '&:hover': {
      backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHighest,
    },
  },
};

export const FormToggleGroup = (props: FormToggleGroupProps) => {
  const {
    name,
    label,
    options,
    required,
    defaultValue,
    disabled,
    showsIf = [],
    onChange,
    sx,
    toggleGroupProps,
  } = props;

  const { control, formState } = useFormContext();
  const errorMsg = formState.errors[name]?.message;
  const hasErrorMsg = typeof errorMsg === 'string';

  const shouldShow = useWatchConditions({ control, showsIf });

  if (!shouldShow) {
    return null;
  }

  return (
    <FormControl error={hasErrorMsg} sx={sx}>
      {!!label && (
        <FormLabel sx={{ fontSize: '0.75rem', marginBottom: 0.5 }}>{label}</FormLabel>
      )}
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        defaultValue={defaultValue ?? options[0]?.value ?? ''}
        render={({ field }) => (
          <ToggleButtonGroup
            exclusive
            size="small"
            disabled={disabled}
            {...toggleGroupProps}
            value={typeof field.value === 'string' ? field.value : ''}
            onChange={(event, nextValue) => {
              // An exclusive choice has no empty state: deselecting the active button
              // yields null, and keeping the current value is the only sane answer.
              if (typeof nextValue !== 'string') {
                return;
              }

              field.onChange(nextValue);
              onChange?.(event, nextValue);
            }}
            sx={groupSx}
          >
            {options.map((option) => (
              <ToggleButton
                key={option.id}
                value={option.value}
                data-testid={`${name}-${option.value}`}
                sx={{
                  gap: 0.75,
                  textTransform: 'none',
                  paddingX: 2,
                  color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
                }}
              >
                {!!option.icon && (
                  <DynamicIcon
                    className={ICON_CLASS_NAME}
                    name={option.icon}
                    library={option.iconLibrary}
                  />
                )}
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      />
      {hasErrorMsg ? <FormHelperText>{errorMsg}</FormHelperText> : null}
    </FormControl>
  );
};
